import {invalidateQueries, q} from "../../../state";
import {
  ComplexityModelId,
  ComplexityModelVersionId,
  ComplexityTier,
  ComplexityTierId,
  CreateComplexityTier,
  UpdateComplexityTier,
} from "platformed-client/types";
import {platformedClient} from "../..";

export async function listAll() {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/complexity/tiers", {});
}

export async function listUsed() {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/complexity/tiers/used", {});
}

/**
 * List all tiers for a complexity model version
 */
export async function list(modelId: ComplexityModelId, versionId: ComplexityModelVersionId): Promise<ComplexityTier[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/tiers",
    {path: {model_id: modelId, version_id: versionId}},
  );
}

/**
 * Create a new tier for a complexity model version
 */
export async function create(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
  tier: CreateComplexityTier,
): Promise<ComplexityTierId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/tiers",
    {path: {model_id: modelId, version_id: versionId}, body: tier},
  );
  await invalidateQueries([
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
    q.tprm.complexityTiers.filter(modelId, versionId),
    q.tprm.complexityTierAutoruns.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Update an existing tier
 */
export async function update(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
  tierId: ComplexityTierId,
  updateData: UpdateComplexityTier,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/tier/{tier_id}",
    {path: {model_id: modelId, version_id: versionId, tier_id: tierId}, body: updateData},
  );
  await invalidateQueries([
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
    q.tprm.complexityTiers.filter(modelId, versionId),
    q.tprm.complexityTierAutoruns.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Delete a tier from a complexity model version
 */
export async function delete_(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
  tierId: ComplexityTierId,
) {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/tier/{tier_id}",
    {path: {model_id: modelId, version_id: versionId, tier_id: tierId}},
  );
  await invalidateQueries([
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
    q.tprm.complexityTiers.filter(modelId, versionId),
    q.tprm.complexityTierAutoruns.filter(modelId, versionId),
  ]);
  return res;
}
